import React, { ComponentType, ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage, MapEntity } from "@mapmycustomers/shared/types/entity";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";

import DataViewButton from "@app/component/DataView/DataViewButton";
import { EntityItemProperties } from "@app/component/RecordCard";
import {
  RecordCount,
  recordsCountTextGetterFactory,
  selectedPinsPaginationTextGetterFactory,
  TextGetters,
} from "@app/component/RecordList";
import CustomizeMenu from "@app/scene/map/components/CustomizeMenu";
import EntityItem from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityItem";
import EntityRecordView from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityRecordView";
import MapModeNoData from "@app/scene/map/components/MapMode/components/Sidebar/components/MapModeNoData";
import MultiPinBackButton from "@app/scene/map/components/sidebar/MultiPinBackButton";
import MultiPinRecordList from "@app/scene/map/components/sidebar/MultiPinRecordList";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import {
  exitLassoMode,
  fetchGroupRecords,
  fetchRecords,
  fetchTerritoryRecords,
  hideMultiPin,
} from "@app/scene/map/store/actions";
import {
  getMultiPin,
  getRecordsListRange,
  isDataViewLoading,
  isLassoLoading,
} from "@app/scene/map/store/selectors";
import { isEntityViewVisible } from "@app/store/entityView";
import { hideEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import styles from "./SelectedPinsSidebar.module.scss";
import Title from "./Title";

const messages = defineMessages({
  search: {
    id: "map.recordsList.search.lassoPlaceholder",
    defaultMessage: "Search records in this selection",
    description: "Records list for lasso mode - search placeholder",
  },
});

type SelectedPinsSidebarProps = {
  beforeTitle?: ReactNode;
  itemComponent: ComponentType<EntityItemProperties<MapEntity>>;
  loading?: boolean;
  onDataViewClick?: () => void;
  onEmailSubscribe?: (item: MapEntity) => void;
  onEmailUnsubscribe?: (item: MapEntity) => void;
  onFetchRecords:
    | typeof fetchGroupRecords.request
    | typeof fetchRecords.request
    | typeof fetchTerritoryRecords.request;
  onRangeChange?: (range: RangeModel) => void;
  onSearch?: (search: string) => void;
  pinGroupsAndRoutesByDefault?: boolean;
  range?: RangeModel;
  records: MapEntity[];
  recordsCount: number;
  showDataView?: boolean;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const SelectedPinsSidebar: React.FC<SelectedPinsSidebarProps> = ({
  beforeTitle,
  dataViewLoading,
  isEntityViewVisible,
  itemComponent,
  lassoLoading,
  loading,
  multiPin,
  onDataViewClick,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onExitLassoMode,
  onFetchRecords,
  onHideEntityView,
  onHideMultiPin,
  onRangeChange,
  onSearch,
  pinGroupsAndRoutesByDefault,
  range,
  records,
  recordsCount,
  recordsRange,
  showDataView = true,
}) => {
  const intl = useIntl();
  const isMultiPinMode = !!multiPin;

  const handleClose = useCallback(() => {
    if (isMultiPinMode) {
      onHideMultiPin();
    } else {
      onHideEntityView();
      onExitLassoMode();
    }
  }, [onExitLassoMode, onHideEntityView, onHideMultiPin, isMultiPinMode]);

  const handleEntityRecordClose = useCallback(() => onHideEntityView(), [onHideEntityView]);

  const handleRangeChange = useCallback(
    (range) => {
      if (onRangeChange) {
        onRangeChange(range);
      } else {
        onFetchRecords({ request: { range } });
      }
    },
    [onFetchRecords, onRangeChange]
  );

  const handleSearch = useCallback(
    (search) => {
      if (onSearch) {
        onSearch(search);
      } else {
        onFetchRecords({ request: { search } });
      }
    },
    [onFetchRecords, onSearch]
  );

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: recordsCountTextGetterFactory(intl),
      paginationTextGetter: selectedPinsPaginationTextGetterFactory(intl),
    }),
    [intl]
  );

  const selectedEntityTypes: EntityTypesSupportedByMapsPage[] = useMemo(() => {
    return Array.from(new Set(records.map(({ entity }) => entity)));
  }, [records]);

  return (
    <BaseSidebar
      beforeTitle={isMultiPinMode ? <MultiPinBackButton /> : beforeTitle}
      className={styles.container}
      menu={
        <CustomizeMenu
          downloadDisabled={records.length === 0}
          visibleEntities={selectedEntityTypes}
        />
      }
      onClose={handleClose}
      title={<Title />}
      visible
      wrapper={isEntityViewVisible}
    >
      <EntityRecordView onClose={handleEntityRecordClose} />
      {!isEntityViewVisible && (
        <>
          {isMultiPinMode ? (
            <MultiPinRecordList itemComponent={EntityItem} />
          ) : (
            <>
              {recordsCount > 0 && (
                <div className={styles.info}>
                  <RecordCount count={textGetters.countTextGetter(recordsCount)} />
                  {showDataView && (
                    <DataViewButton
                      entityCount={recordsCount}
                      loading={dataViewLoading}
                      onClick={onDataViewClick}
                    />
                  )}
                </div>
              )}

              <RecordList<MapEntity>
                idGetter={mapEntityIdGetter}
                itemComponent={itemComponent}
                loading={loading || lassoLoading}
                noData={<MapModeNoData />}
                onEmailSubscribe={onEmailSubscribe}
                onEmailUnsubscribe={onEmailUnsubscribe}
                onRangeChange={handleRangeChange}
                onSearch={handleSearch}
                pinGroupsAndRoutesByDefault={pinGroupsAndRoutesByDefault}
                range={range ?? recordsRange}
                records={records}
                searchPlaceholder={intl.formatMessage(messages.search)}
                showCount={false}
                textGetters={textGetters}
                total={recordsCount}
              />
            </>
          )}
        </>
      )}
    </BaseSidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataViewLoading: isDataViewLoading(state),
  isEntityViewVisible: isEntityViewVisible(state),
  lassoLoading: isLassoLoading(state),
  multiPin: getMultiPin(state),
  recordsRange: getRecordsListRange(state),
});

const mapDispatchToProps = {
  onExitLassoMode: exitLassoMode,
  onHideEntityView: hideEntityView,
  onHideMultiPin: hideMultiPin,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedPinsSidebar);
